import { useEffect, useRef } from "react";

export default function HeroSection() {
  const scrollToSection = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    let w = (canvas.width = canvas.offsetWidth);
    let h = (canvas.height = canvas.offsetHeight);

    const particles: { x: number; y: number; vx: number; vy: number; r: number; alpha: number }[] = [];
    for (let i = 0; i < 70; i++) {
      particles.push({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 1.8 + 0.4,
        alpha: Math.random() * 0.4 + 0.1,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(169,186,171,${p.alpha})`;
        ctx.fill();
      });
      particles.forEach((p1, i) => {
        particles.slice(i + 1).forEach((p2) => {
          const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
          if (dist < 130) {
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(169,186,171,${0.12 * (1 - dist / 130)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });
      animId = requestAnimationFrame(draw);
    };

    draw();
    const resize = () => {
      w = canvas.width = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
    };
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
      style={{ background: "linear-gradient(160deg, #1a0d28 0%, #4d316c 45%, #2c3e30 80%, #1a2a1e 100%)" }}
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full opacity-60" />
      <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] rounded-full opacity-15 blur-[100px]" style={{ background: "#705a89" }} />
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full opacity-10 blur-[80px]" style={{ background: "#a9baab" }} />
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/50" />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-10 text-center flex flex-col items-center pt-32 pb-16">
        {/* Badge */}
        <div
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium mb-8 animate-fadeIn"
          style={{ background: "rgba(169,186,171,0.12)", border: "1px solid rgba(169,186,171,0.25)", color: "#a9baab" }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-sage animate-pulse inline-block"></span>
          Operational AI Systems
        </div>

        {/* Headline — Apple scale */}
        <h1
          className="text-5xl md:text-7xl lg:text-8xl font-semibold leading-[1.05] tracking-tight text-white mb-6 animate-fadeUp"
          style={{ animationDelay: "0.1s", animationFillMode: "backwards" }}
        >
          Intelligence That{" "}
          <br className="hidden md:block" />
          <span style={{ color: "#a9baab" }}>Protects</span> and{" "}
          <span style={{ background: "linear-gradient(90deg, #c4aad8, #a9baab)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Performs.
          </span>
        </h1>

        <p
          className="text-base md:text-xl text-white/60 max-w-2xl mx-auto mb-10 leading-relaxed animate-fadeUp"
          style={{ animationDelay: "0.2s", animationFillMode: "backwards" }}
        >
          Patronum AI builds operational systems that capture every opportunity, automate every follow-up, and ensure your business runs without gaps.
        </p>

        <div
          className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fadeUp"
          style={{ animationDelay: "0.3s", animationFillMode: "backwards" }}
        >
          <button
            onClick={() => scrollToSection("#contact")}
            className="px-9 py-4 rounded-full font-semibold text-sm whitespace-nowrap cursor-pointer transition-all duration-200 hover:opacity-90 hover:scale-105"
            style={{ background: "#a9baab", color: "#1a0d28" }}
          >
            Get Your System Audit
          </button>
          <button
            onClick={() => scrollToSection("#solution")}
            className="px-9 py-4 rounded-full font-semibold text-sm whitespace-nowrap cursor-pointer transition-all duration-200 flex items-center gap-2 hover:gap-3"
            style={{ border: "1px solid rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.85)" }}
          >
            See How It Works
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>

        {/* Stats row */}
        <div
          className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-10 md:gap-20 w-full animate-fadeUp"
          style={{ animationDelay: "0.45s", animationFillMode: "backwards" }}
        >
          {[
            { value: "3×", label: "Faster Lead Response" },
            { value: "85%", label: "Reduction in Manual Work" },
            { value: "32%", label: "More Booked Jobs" },
          ].map((stat, i) => (
            <div key={stat.label} className="flex flex-col items-center gap-1">
              {i > 0 && <div className="hidden sm:block absolute w-px h-8 bg-white/10" />}
              <span className="text-3xl md:text-4xl font-semibold tracking-tight" style={{ color: "#a9baab" }}>
                {stat.value}
              </span>
              <span className="text-xs text-white/40 tracking-wide">{stat.label}</span>
            </div>
          ))}
        </div>

        {/* Hero visual — dashboard mockup */}
        <div
          className="mt-20 w-full max-w-5xl animate-fadeUp"
          style={{ animationDelay: "0.55s", animationFillMode: "backwards" }}
        >
          <div
            className="relative w-full rounded-2xl overflow-hidden"
            style={{ border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)" }}
          >
            {/* Browser chrome */}
            <div className="flex items-center gap-2 px-5 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              <div className="w-3 h-3 rounded-full" style={{ background: "rgba(255,255,255,0.12)" }} />
              <div className="w-3 h-3 rounded-full" style={{ background: "rgba(255,255,255,0.12)" }} />
              <div className="w-3 h-3 rounded-full" style={{ background: "rgba(255,255,255,0.12)" }} />
              <div className="flex-1 mx-6 h-6 rounded-md flex items-center justify-center" style={{ background: "rgba(255,255,255,0.05)" }}>
                <span className="text-xs" style={{ color: "rgba(255,255,255,0.2)" }}>patronum.ai/dashboard</span>
              </div>
            </div>
            <img
              src="https://public.readdy.ai/ai/img_res/c8a7064564c41c3301ec16fd4c0dd809.jpg"
              alt="Patronum AI operational dashboard"
              className="w-full h-auto object-cover object-top"
              style={{ minHeight: "300px", maxHeight: "560px" }}
            />
            {/* Gradient fade at bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#1a0d28] to-transparent" />
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-30">
        <span className="text-white text-xs tracking-[0.2em] uppercase">Scroll</span>
        <div className="w-px h-8 bg-white/40 animate-floatSlow" />
      </div>
    </section>
  );
}
